import { Opportunity } from "@/opportunity/opportunity";
import {
  DropdownMenu,
  DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeftRight,
  ChartLine,
  CheckIcon,
  Copy,
  CopyCheck,
  ExternalLink, LinkIcon,
  Send,
  Eye,
  EyeOff
} from "lucide-react";
import React, { useState } from "react";

import { useStatusDetails } from "@/opportunity/useOpportunityStatuses";
import ChangeStatusModal from "@/opportunity/ChangeStatusModal";
import QuickTooltip from "@/components/quick-tooltip";
import { useGenerateShareableLink, useNonStaffUsers } from "@/opportunity/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Card } from "@/components/ui/card";
import {
  useGenerateAndCopyOpportunityEmail
} from "@/opportunity/opportunity_detail_page/useGenerateAndCopyOpportunityEmail";
import { MultiSelect, OptionType } from "@/components/ui/multi-select";
import { toast } from 'sonner';
import SendEmailDialog from "@/opportunity/opportunity_detail_page/SendEmailDialog";

const OpportunityActionsDropdown = ({
                                      opportunity,
                                      showStaffView,
                                      setShowStaffView
                                    }: {
  opportunity: Opportunity;
  showStaffView: boolean;
  setShowStaffView: (show: boolean) => void;
}) => {

  const status = useStatusDetails(opportunity.status)
  const notShareable = !status.visible_to_customers;

  const {
    isGeneratingEmail,
    generatedEmailHtml,
    generateEmailContent,
    isGeneratedEmailHtmlCopied,
    setIsGeneratedEmailHtmlCopied,
    copyEmailContent,
    copyEmailSubject,
    emailSubject,
    isEmailSubjectCopied,
    setIsEmailSubjectCopied,
  } = useGenerateAndCopyOpportunityEmail(opportunity);

  const [isEmailPreviewDialogOpen, setIsEmailPreviewDialogOpen] = useState(false);
  const [isSendEmailDialogOpen, setIsSendEmailDialogOpen] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = React.useState(false);
  const [changeStatusModalOpen, setChangeStatusModalOpen] = useState(false);

  const {
    generateAndCopyShareableLink,
    isPending: isGeneratingShareLink
  } = useGenerateShareableLink(
    opportunity.id,
    () => {
      setShareLinkCopied(true);
      toast.message("Share link copied to clipboard!");
      console.log("Share link copied to clipboard!");
    },
    () => {
      setShareLinkCopied(false);
      toast.error("Error generating or copying share link");
      console.error("Error generating or copying share link");
    }
  );

  const { data: nonStaffUsers } = useNonStaffUsers(opportunity.id);
  const [selectedUsers, setSelectedUsers] = useState<OptionType[]>([]);
  const [emailsCopied, setEmailsCopied] = useState(false);

  const userOptions: OptionType[] = nonStaffUsers
    .filter(user => !!user.email)
    .map(user => ({
      label: `${user.first_name} ${user.last_name} (${user.email})`,
      value: user.username
    }));

  const selectedEmails = selectedUsers
    .map(option => {
      const user = nonStaffUsers.find(u => u.username === option.value);
      return user?.email;
    })
    .filter(Boolean)
    .join(', ');

  const copySelectedEmails = async () => {
    if (selectedEmails) {
      await navigator.clipboard.writeText(selectedEmails);
      setEmailsCopied(true);
      setTimeout(() => setEmailsCopied(false), 2000);
    }
  };

  return (
    <>
      <Dialog
        open={isEmailPreviewDialogOpen}
        onOpenChange={(isOpen) => {
          setIsEmailPreviewDialogOpen(isOpen);
          setIsGeneratedEmailHtmlCopied(false);
          setIsEmailSubjectCopied(false);
          setSelectedUsers([]);
          setEmailsCopied(false);
        }}
      >
        <DropdownMenu
          onOpenChange={() => {
            setShareLinkCopied(false);
          }}
          modal={false}
        >
          <DropdownMenuTrigger asChild>
            <Button variant="default" size="sm" className="p-2 bg-thalo-blue text-white w-fit text-xs">
              Staff settings
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="" align="end" forceMount side="bottom">
            <DropdownMenuLabel>
              Manage
            </DropdownMenuLabel>
            <DropdownMenuItem className="cursor-pointer" asChild>
              <button
                onClick={() => setChangeStatusModalOpen(true)}
                className="w-full"
              >
                <ArrowLeftRight className="mr-2 h-4 w-4"/>
                <span>Change status</span>
              </button>
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer" asChild>
              <button
                onClick={() => setShowStaffView(!showStaffView)}
                className="w-full"
              >
                {showStaffView ? <EyeOff className="mr-2 h-4 w-4"/> : <Eye className="mr-2 h-4 w-4"/>}
                <span>
                  {showStaffView ? "Show regular user view" : "Show staff view"}
                </span>
              </button>
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer" asChild>
              <a target="_blank"
                 href={`${import.meta.env.VITE_API_URL.replace(/\/api\/$/, "")}/admin/opportunities/opportunity/${opportunity.id}/change/`}>
                <ExternalLink className="mr-2 h-4 w-4"/>
                <span>Manage issue in ThaloOS</span>
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer" asChild disabled={!opportunity.grafana_url}>
              <a
                href={opportunity.grafana_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ChartLine className="mr-2 h-4 w-4"/>
                <span>View detection in Grafana</span>
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator/>
            <DropdownMenuLabel>
              Share
            </DropdownMenuLabel>
            {
              notShareable && (
                <div className="w-full flex flex-row justify-center gap-1">
                  <span className="text-sm">🙅 Not shareable!</span>
                  <QuickTooltip triggerClassname="text-sm" text={
                    <div className="text-sm flex flex-col gap-2">
                      <p>Cannot share an issue in status "{opportunity.status}".</p>
                      <p>Change the issue's status to share it, or update ThaloOS to indicate that
                        "{opportunity.status}" issues are visible to customers.</p>
                    </div>
                  }>
                    <span>Why?</span>
                  </QuickTooltip>
                </div>

              )
            }
            <DropdownMenuItem className="cursor-pointer" asChild disabled={notShareable}
                              onSelect={event => event.preventDefault()}>
              <button onClick={() => generateAndCopyShareableLink()} className="w-full">
                {
                  isGeneratingShareLink ? (
                    <Spinner className="mr-2 h-4 w-4"/>
                  ) : shareLinkCopied ? (
                    <CheckIcon className="mr-2 h-4 w-4"/>
                  ) : (
                    <LinkIcon className="mr-2 h-4 w-4"/>
                  )
                }
                <span>
                  {
                    isGeneratingShareLink ? "Generating link..." :
                    shareLinkCopied ? "Link copied!" : "Generate and copy shareable link"
                  }
                </span>
              </button>
            </DropdownMenuItem>
            <DialogTrigger asChild>
              <DropdownMenuItem
                className="cursor-pointer"
                disabled={notShareable}
                onSelect={async (event) => {
                  event.preventDefault();
                  await generateEmailContent();
                }}
              >
                <div className="w-full flex items-center">
                  {
                    isGeneratingEmail ? <Spinner className="mr-2 h-4 w-4"/> : <Copy className="mr-2 h-4 w-4"/>
                  }
                  <span>
                    Copy email content
                  </span>
                </div>
              </DropdownMenuItem>
            </DialogTrigger>
            <DropdownMenuItem
              className="cursor-pointer"
              disabled={notShareable}
              onSelect={() => setIsSendEmailDialogOpen(true)}
            >
              <Send className="mr-2 h-4 w-4"/>
              <span>Send email</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DialogContent
          className="sm:max-w-none w-[60%] h-[80vh] p-8 flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>Issue email preview</DialogTitle>
            <DialogDescription>
              Below is how the generated email should appear once you paste it into your email application.
            </DialogDescription>
            <button className="text-left" onClick={copyEmailSubject}>
              <Card className="p-2 flex flex-row gap-2 justify-between items-center hover:bg-gray-50">
                <div>
                  <h3 className="font-semibold">Subject (click to copy this too!)</h3>
                  <span className="grow text-sm">
                    {emailSubject}
                  </span>
                </div>
                <div
                  title="Copy email subject"
                  className="aspect-square h-fit p-1 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  {
                    isEmailSubjectCopied ? <CopyCheck size={16}/> : <Copy size={16}/>
                  }
                </div>
              </Card>
            </button>

            <div className="flex flex-row items-start gap-2">
              <MultiSelect
                options={userOptions}
                selected={selectedUsers}
                onChange={setSelectedUsers}
                placeholder="Select recipients..."
                showSelectAllAndNone={true}
              />
              {selectedEmails && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copySelectedEmails}
                  className="w-full"
                >
                  {emailsCopied ? <CopyCheck className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                  {emailsCopied ? "Emails copied!" : `Copy ${selectedUsers.length} email address${selectedUsers.length > 1 ? 'es': ''}`}
                </Button>
              )}
            </div>
          </DialogHeader>
          <iframe className="w-full h-full" srcDoc={generatedEmailHtml}/>
          <Button variant="thalo" onClick={copyEmailContent}>
            {
              isGeneratedEmailHtmlCopied ? <CopyCheck className="mr-2 h-4 w-4"/> : <Copy className="mr-2 h-4 w-4"/>
            }
            <span>
              {
                isGeneratedEmailHtmlCopied ? "Email content copied! Paste it into an email." : "Copy email content"
              }
            </span>
          </Button>
        </DialogContent>
      </Dialog>

      <ChangeStatusModal
        isOpen={changeStatusModalOpen}
        onClose={() => setChangeStatusModalOpen(false)}
        opportunity={opportunity}
      />

      <SendEmailDialog
        opportunity={opportunity}
        open={isSendEmailDialogOpen}
        onOpenChange={setIsSendEmailDialogOpen}
      />
    </>
  )
}

export default OpportunityActionsDropdown;